import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import QRCode from 'react-qr-code';
import './styles.scss';
import l from '../../../../lang';
import Dialog from '../../../common/dialog';
import Input from '../../../common/input';
import ButtonCustom from '../../../common/__deprecated__/button-custom';
import MessageError from '../../../common/message-error';
import Loading from '../../../common/loading';
import { Button } from '../../../common/button';
import Switch from '../../../common/switch';
import { setupTwoFactor, confirmTwoFactor, disableTwoFactor, cleanTwoFactorState } from '../../../../store/profile/actions';
import { useDashboard } from '../../../../store/dashboard';

const TwoFactorAuth = ({
  twoFactorLoading,
  twoFactorError,
  twoFactorErrorInfo,
  twoFactorSetup,
  twoFactorEnabled,
  userMe,
  fnDisableTwoFactor,
  fnSetupTwoFactor,
  fnConfirmTwoFactor,
  fnCleanTwoFactor,
}) => {
  const { showTwoFactorAuthSetup } = useDashboard();
  const [open, setOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [copied, setCopied] = useState(false);

  const isEnabled = Boolean(userMe?.totpEnabled ?? twoFactorEnabled);
  const isConfigured = Boolean(userMe?.totpConfigured ?? twoFactorSetup?.secret);

  useEffect(() => {
    if (showTwoFactorAuthSetup) {
      handleOpen();
    }
  }, [showTwoFactorAuthSetup]);

  useEffect(() => {
    if (open && isEnabled) {
      setOpen(false);
      setCode('');
      setCopied(false);
      fnCleanTwoFactor();
    }
  }, [isEnabled, open, fnCleanTwoFactor]);

  useEffect(() => {
    if (disableOpen && !isEnabled) {
      setDisableOpen(false);
      setDisableCode('');
      fnCleanTwoFactor();
    }
  }, [isEnabled, disableOpen, fnCleanTwoFactor]);

  const handleOpen = () => {
    setOpen(true);
    setCode('');
    setCopied(false);
    fnCleanTwoFactor();
    fnSetupTwoFactor();
  };

  const handleClose = () => {
    setOpen(false);
    setCode('');
    setCopied(false);
    fnCleanTwoFactor();
  };

  const handleDisableOpen = () => {
    setDisableOpen(true);
    setDisableCode('');
    fnCleanTwoFactor();
  };

  const handleDisableClose = () => {
    setDisableOpen(false);
    setDisableCode('');
    fnCleanTwoFactor();
  };

  const handleCopy = async () => {
    if (!twoFactorSetup?.secret) {
      return;
    }

    try {
      await navigator.clipboard.writeText(twoFactorSetup.secret);
      setCopied(true);
    } catch (_) {
      setCopied(false);
    }
  };

  const handleConfirm = () => {
    if (!code) {
      return;
    }
    fnConfirmTwoFactor(code.trim());
  };

  const handleDisableConfirm = () => {
    if (!disableCode) {
      return;
    }
    fnDisableTwoFactor(disableCode.trim());
  };

  const handleToggle = () => {
    if (isEnabled) {
      handleDisableOpen();
    } else {
      handleOpen();
    }
  };

  return (
    <div className="two-factor">
      <div className="two-factor__section">
        <div className="two-factor__section__title">{l('twoFactor.title')}</div>
      </div>
      <div className="two-factor__status">
        <span className="two-factor__status__label">{l('twoFactor.statusLabel')}</span>
        <span className="two-factor__status__value">
          {isEnabled ? l('twoFactor.status.enabled') : l('twoFactor.status.disabled')}
        </span>
        <div className="two-factor__status__switch">
          <Switch checked={isEnabled} onChange={handleToggle} />
        </div>
      </div>
      {!isEnabled && (
        <div className="two-factor__actions">
          <ButtonCustom
            text={isConfigured ? l('twoFactor.resumeButton') : l('twoFactor.setupButton')}
            onClick={handleOpen}
          />
        </div>
      )}
      {open && (
        <Dialog
          type="sm"
          fullScreenMobile
          title={l('twoFactor.modal.title')}
          handleClose={handleClose}
          body={
            <div className="two-factor__modal">
              {twoFactorLoading && !twoFactorSetup && (
                <div className="two-factor__loading">
                  <Loading />
                </div>
              )}
              {twoFactorError && !twoFactorSetup && (
                <div className="two-factor__error">
                  <MessageError errorInfo={twoFactorErrorInfo} />
                </div>
              )}
              {twoFactorSetup && (
                <>
                  <div className="two-factor__instructions">{l('twoFactor.modal.qrInstruction')}</div>
                  <div className="two-factor__qr">
                    <QRCode value={twoFactorSetup.otpAuthUrl} size={160} />
                  </div>
                  <div className="two-factor__secret">
                    <div className="two-factor__secret__label">{l('twoFactor.modal.manualCode')}</div>
                    <div className="two-factor__secret__value">{twoFactorSetup.secret}</div>
                    <Button onClick={handleCopy} textTransformNone>
                      {copied ? l('twoFactor.modal.copied') : l('twoFactor.modal.copy')}
                    </Button>
                  </div>
                  <Input
                    name="twoFactorCode"
                    label={l('twoFactor.modal.codeLabel')}
                    value={code}
                    maxLength={6}
                    onChange={e => setCode(e.target.value)}
                  />
                  <div className="two-factor__helper">{l('twoFactor.modal.helper')}</div>
                  {twoFactorError && (
                    <div className="two-factor__error">
                      <MessageError errorInfo={twoFactorErrorInfo} />
                    </div>
                  )}
                  <div className="two-factor__buttons">
                    <ButtonCustom text={l('twoFactor.modal.cancel')} size="half-left-square" onClick={handleClose} />
                    <ButtonCustom
                      text={l('twoFactor.modal.confirm')}
                      variant="contained"
                      size="half-right-square"
                      onClick={handleConfirm}
                      disabled={twoFactorLoading || !code}
                    />
                  </div>
                </>
              )}
            </div>
          }
        />
      )}
      {disableOpen && (
        <Dialog
          type="sm"
          fullScreenMobile
          title={l('twoFactor.disable.title')}
          handleClose={handleDisableClose}
          body={
            <div className="two-factor__modal">
              <div className="two-factor__instructions">{l('twoFactor.disable.helper')}</div>
              <Input
                name="twoFactorDisableCode"
                label={l('twoFactor.disable.codeLabel')}
                value={disableCode}
                maxLength={6}
                onChange={e => setDisableCode(e.target.value)}
              />
              {twoFactorError && (
                <div className="two-factor__error">
                  <MessageError errorInfo={twoFactorErrorInfo} />
                </div>
              )}
              <div className="two-factor__buttons">
                <ButtonCustom text={l('twoFactor.disable.cancel')} size="half-left-square" onClick={handleDisableClose} />
                <ButtonCustom
                  text={l('twoFactor.disable.confirm')}
                  variant="contained"
                  size="half-right-square"
                  onClick={handleDisableConfirm}
                  disabled={twoFactorLoading || !disableCode}
                />
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

TwoFactorAuth.propTypes = {
  twoFactorLoading: PropTypes.bool,
  twoFactorError: PropTypes.bool,
  twoFactorErrorInfo: PropTypes.object,
  twoFactorSetup: PropTypes.object,
  twoFactorEnabled: PropTypes.bool,
  userMe: PropTypes.object,
  fnDisableTwoFactor: PropTypes.func,
  fnSetupTwoFactor: PropTypes.func,
  fnConfirmTwoFactor: PropTypes.func,
  fnCleanTwoFactor: PropTypes.func,
};

const mapStateToProps = state => ({
  twoFactorLoading: state.profile.twoFactorLoading,
  twoFactorError: state.profile.twoFactorError,
  twoFactorErrorInfo: state.profile.twoFactorErrorInfo,
  twoFactorSetup: state.profile.twoFactorSetup,
  twoFactorEnabled: state.profile.twoFactorEnabled,
  userMe: state.dashboard.userMe,
});

const mapDispatchToProps = dispatch => ({
  fnSetupTwoFactor: () => dispatch(setupTwoFactor()),
  fnConfirmTwoFactor: code => dispatch(confirmTwoFactor(code)),
  fnDisableTwoFactor: code => dispatch(disableTwoFactor(code)),
  fnCleanTwoFactor: () => dispatch(cleanTwoFactorState()),
});

export default connect(mapStateToProps, mapDispatchToProps)(TwoFactorAuth);
